import { Mail, Lock } from "lucide-react";

/**
 * LoginBrandPreview — a miniature, non-interactive mock of the sign-in page
 * rendered from UNSAVED branding form values, so an admin can see exactly what
 * their users will land on before committing.
 *
 * Deliberately faithful to the REAL /login layout, including what branding
 * does NOT change: the right-hand panel stays platform navy and the header
 * falls back to the platform logo when no custom logo is uploaded. Only the
 * logo, the subtitle line and the primary-coloured button are tenant-driven.
 */
export function LoginBrandPreview({ brand = {}, partnerName = "Partner", className = "" }) {
  const primary = brand.primary_color || "#206bc4";
  const logo = brand.logo_url_light || brand.logo_url;
  const name = brand.brand_name || partnerName;
  const subtitle = brand.brand_subtitle || "Admin Dashboard";

  return (
    <div className={className} data-testid="login-brand-preview">
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-3 py-1.5">
          <span className="h-2 w-2 rounded-full bg-slate-300" />
          <span className="h-2 w-2 rounded-full bg-slate-300" />
          <span className="h-2 w-2 rounded-full bg-slate-300" />
          <span className="ml-2 truncate text-[10px] text-slate-400">Sign in — {name}</span>
        </div>

        <div className="grid grid-cols-5">
          <div className="col-span-3 p-4">
            <div className="mb-3 flex h-7 items-center">
              {logo
                ? <img src={logo} alt={name} className="h-full w-auto object-contain" data-testid="login-preview-logo" />
                : <img src="/brand/conduit-v2-light.png?v=3" alt="Conduit"
                       className="h-full w-auto object-contain" data-testid="login-preview-default-logo" />}
            </div>
            <div className="text-[13px] font-bold text-slate-900">Welcome back</div>
            <div className="mt-0.5 text-[10px] text-slate-400" data-testid="login-preview-subtitle">
              Sign in to your control center.
            </div>

            <div className="mt-3 space-y-2">
              {[Mail, Lock].map((Icon, i) => (
                <div key={i} className="flex items-center gap-2 rounded-md border border-slate-200 px-2 py-1.5">
                  <Icon className="h-3 w-3 shrink-0 text-slate-300" />
                  <span className="h-1.5 w-20 rounded bg-slate-100" />
                </div>
              ))}
              <div
                className="flex h-6 items-center justify-center rounded-md text-[10px] font-semibold text-white"
                style={{ backgroundColor: primary }}
                data-testid="login-preview-button"
              >
                Sign in
              </div>
            </div>
          </div>

          {/* Platform navy — the sign-in banner is NOT tenant-coloured. */}
          <div className="col-span-2 flex flex-col justify-end bg-[#0f2a4a] p-3 text-white">
            <div className="text-[11px] font-bold leading-tight">One control center for your team.</div>
            <div className="mt-1 text-[9px] leading-snug text-white/75">
              Users, permissions and apps in one secure dashboard.
            </div>
          </div>
        </div>
      </div>
      <p className="mt-1.5 text-[11px] text-slate-400">
        Preview only — nothing is saved until you press Save branding. On the sign-in page, branding drives
        the logo, the <span className="font-medium text-slate-500">{subtitle}</span> browser title and the
        button colour; the banner panel stays platform navy.
      </p>
    </div>
  );
}
